import React, { useState, useEffect, useCallback } from "react";
import styles from "./FiscalDeviceDiagnosticsScreen.module.scss";
import classNames from "classnames";
import moment from "moment-timezone";
import fiscalDeviceService from "../../../../apis/services/admin/fiscalDeviceService";

// Time range options
const TIME_RANGES = [
  { label: "1h", value: 60 * 60 * 1000 },
  { label: "24h", value: 24 * 60 * 60 * 1000 },
  { label: "7d", value: 7 * 24 * 60 * 60 * 1000 },
  { label: "30d", value: 30 * 24 * 60 * 60 * 1000 },
  { label: "Sve", value: 0 },
];

// Event type options
const EVENT_TYPES = [
  { value: "", label: "Svi tipovi" },
  { value: "PIN_SYNC_COMMAND_CREATED", label: "PIN Sync kreiran" },
  { value: "PIN_MISMATCH_DETECTED", label: "PIN nepodudaranje" },
  { value: "PHYSICAL_INTERVENTION_REQUIRED", label: "Potrebna intervencija" },
  { value: "DEADLOCK_DETECTED", label: "Deadlock" },
  { value: "HIGH_MEMORY_USAGE", label: "Visoka memorija" },
  { value: "CARD_LOCKED", label: "Kartica zaključana" },
  { value: "WRONG_PIN_ENTERED", label: "Pogrešan PIN" },
  { value: "ORPHANED_AUDIT_PACKAGES", label: "Orphan paketi" },
  { value: "DEVICE_OFFLINE_LONG", label: "Uređaj offline" },
  { value: "RECOVERY_FAILED", label: "Neuspešan oporavak" },
];

// Severity options
const SEVERITIES = [
  { value: "", label: "Sve ozbiljnosti" },
  { value: "CRITICAL", label: "🔴 Kritično" },
  { value: "ERROR", label: "❌ Greška" },
  { value: "WARNING", label: "⚠️ Upozorenje" },
  { value: "INFO", label: "ℹ️ Info" },
];

// Severity config
const SEVERITY_CONFIG = {
  CRITICAL: { color: "#B71C1C", bgColor: "#FFEBEE", icon: "🔴", label: "Kritično" },
  ERROR: { color: "#C62828", bgColor: "#FFEBEE", icon: "❌", label: "Greška" },
  WARNING: { color: "#F57C00", bgColor: "#FFF3E0", icon: "⚠️", label: "Upozorenje" },
  INFO: { color: "#1976D2", bgColor: "#E3F2FD", icon: "ℹ️", label: "Info" },
};

// Event type labels
const EVENT_TYPE_LABELS = {
  PIN_SYNC_COMMAND_CREATED: { label: "PIN Sync", icon: "🔄" },
  PIN_MISMATCH_DETECTED: { label: "PIN Mismatch", icon: "🔑" },
  PHYSICAL_INTERVENTION_REQUIRED: { label: "Intervencija", icon: "🛠️" },
  DEADLOCK_DETECTED: { label: "Deadlock", icon: "🔒" },
  HIGH_MEMORY_USAGE: { label: "Memorija", icon: "💾" },
  CARD_LOCKED: { label: "Kartica", icon: "🔐" },
  WRONG_PIN_ENTERED: { label: "Pogrešan PIN", icon: "❌" },
  ORPHANED_AUDIT_PACKAGES: { label: "Orphan", icon: "📦" },
  DEVICE_OFFLINE_LONG: { label: "Offline", icon: "📴" },
  RECOVERY_FAILED: { label: "Recovery", icon: "⚠️" },
  OTHER: { label: "Ostalo", icon: "📝" },
};

// Helper functions
const formatDateTime = (timestamp) => {
  if (!timestamp) return "-";
  return moment(timestamp).tz(moment.tz.guess()).format("DD.MM.YYYY HH:mm:ss");
};

const formatRelativeTime = (timestamp) => {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "Upravo sada";
  if (minutes < 60) return `Pre ${minutes} min`;
  if (hours < 24) return `Pre ${hours}h`;
  if (days < 7) return `Pre ${days} dana`;
  return formatDateTime(timestamp);
};

const FiscalDeviceDiagnosticsScreen = ({ id }) => {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(TIME_RANGES[2].value); // 7 days default
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch diagnostic logs
  const fetchLogs = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const params = { limit: 500 };
      if (timeRange > 0) {
        params.from = Date.now() - timeRange;
      }
      if (eventTypeFilter) {
        params.eventType = eventTypeFilter;
      }
      if (severityFilter) {
        params.severity = severityFilter;
      }

      const response = await fiscalDeviceService.fetchDiagnosticLogs(id, params);
      setLogs(response?.data?.logs || []);
    } catch (err) {
      console.error("Error fetching diagnostic logs:", err);
      setError("Greška pri učitavanju dijagnostičkih logova");
    } finally {
      setLoading(false);
    }
  }, [id, timeRange, eventTypeFilter, severityFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Count by severity
  const counts = {
    critical: logs.filter((l) => l.severity === "CRITICAL").length,
    error: logs.filter((l) => l.severity === "ERROR").length,
    warning: logs.filter((l) => l.severity === "WARNING").length,
    info: logs.filter((l) => l.severity === "INFO").length,
  };

  // Render severity count card
  const renderSeverityCard = (severity, count) => {
    const config = SEVERITY_CONFIG[severity];
    return (
      <div
        className={styles.severityCard}
        style={{ backgroundColor: config.bgColor }}
      >
        <span className={styles.severityCount} style={{ color: config.color }}>
          {count}
        </span>
        <span className={styles.severityLabel} style={{ color: config.color }}>
          {config.icon} {config.label}
        </span>
      </div>
    );
  };

  // Render severity badge
  const renderSeverityBadge = (severity) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.INFO;
    return (
      <span
        className={styles.badge}
        style={{ backgroundColor: config.bgColor, color: config.color }}
      >
        {config.label}
      </span>
    );
  };

  // Render event type badge
  const renderEventTypeBadge = (eventType) => {
    const config = EVENT_TYPE_LABELS[eventType] || { label: eventType, icon: "📝" };
    return (
      <span className={styles.eventBadge}>
        {config.icon} {config.label}
      </span>
    );
  };

  // Render loading state
  const renderLoading = () => (
    <div className={styles.loadingState}>
      <div className={styles.spinner} />
      <p>Učitavanje...</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className={styles.errorState}>
      <p>⚠️ {error}</p>
      <button className={styles.retryButton} onClick={fetchLogs}>
        Pokušaj ponovo
      </button>
    </div>
  );

  // Render empty state
  const renderEmpty = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>✅</div>
      <p>Nema dijagnostičkih događaja za izabrani period</p>
    </div>
  );

  // Render logs table
  const renderLogsTable = () => {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (logs.length === 0) return renderEmpty();

    return (
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.expandColumn}></th>
              <th>Vreme</th>
              <th>Ozbiljnost</th>
              <th>Tip</th>
              <th>Poruka</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <React.Fragment key={log.id}>
                <tr
                  className={classNames(
                    styles.tableRow,
                    expandedRow === log.id && styles.tableRowExpanded
                  )}
                  onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                >
                  <td className={styles.expandColumn}>
                    <button className={styles.expandButton}>
                      {expandedRow === log.id ? "▼" : "▶"}
                    </button>
                  </td>
                  <td>
                    <div className={styles.timeCell}>
                      <span className={styles.relativeTime}>
                        {formatRelativeTime(log.timestamp)}
                      </span>
                      <span className={styles.absoluteTime}>
                        {formatDateTime(log.timestamp)}
                      </span>
                    </div>
                  </td>
                  <td>{renderSeverityBadge(log.severity)}</td>
                  <td>{renderEventTypeBadge(log.eventType)}</td>
                  <td>
                    <span className={styles.messageCell}>{log.message}</span>
                  </td>
                </tr>
                {expandedRow === log.id && (
                  <tr className={styles.detailsRow}>
                    <td colSpan={5}>
                      <div className={styles.detailsContent}>
                        <h4>Detalji</h4>
                        <p className={styles.fullMessage}>{log.message}</p>
                        {log.details && (
                          <pre className={styles.detailsJson}>
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={styles.content}>
      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        {renderSeverityCard("CRITICAL", counts.critical)}
        {renderSeverityCard("ERROR", counts.error)}
        {renderSeverityCard("WARNING", counts.warning)}
        {renderSeverityCard("INFO", counts.info)}
      </div>

      {/* Critical Alert Banner */}
      {counts.critical > 0 && (
        <div className={styles.criticalAlert}>
          <span className={styles.alertIcon}>🚨</span>
          <span>{counts.critical} kritičnih događaja zahteva pažnju!</span>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filtersSection}>
        {/* Time Range */}
        <div className={styles.timeRangeSelector}>
          {TIME_RANGES.map((range) => (
            <button
              key={range.label}
              className={classNames(
                styles.timeRangeButton,
                timeRange === range.value && styles.timeRangeButtonActive
              )}
              onClick={() => setTimeRange(range.value)}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Event Type Filter */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Tip događaja</label>
          <select
            className={styles.filterSelect}
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
          >
            {EVENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Severity Filter */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Ozbiljnost</label>
          <select
            className={styles.filterSelect}
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            {SEVERITIES.map((sev) => (
              <option key={sev.value} value={sev.value}>
                {sev.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs Table */}
      {renderLogsTable()}

      {/* Refresh Button */}
      <div className={styles.refreshSection}>
        <button
          className={styles.refreshButton}
          onClick={fetchLogs}
          disabled={loading}
        >
          {loading ? "Učitavanje..." : "Osveži"}
        </button>
      </div>
    </div>
  );
};

export default FiscalDeviceDiagnosticsScreen;

